import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t, tDynamic, dateTime, money } from '../i18n/index.ts';
import { api } from '../lib/api.ts';
import { ErrorView, Loading } from '../components/ui.tsx';
import type { NotificationView } from '../../server/domain/notifications.ts';

function text(n: NotificationView): string {
  const d = n.data as Record<string, string | number>;
  return tDynamic(
    `notifications.kinds.${n.kind}`,
    {
      ...Object.fromEntries(Object.entries(d).map(([k, v]) => [k, String(v)])),
      amount: typeof d.amountCents === 'number' ? money(d.amountCents) : '',
      action: d.action === 'check' ? 'check' : 'fold',
    },
    n.kind,
  );
}

export function NotificationsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['notifications'], queryFn: () => api.get<{ items: NotificationView[]; unread: number }>('/api/notifications') });
  const mark = useMutation({
    mutationFn: (ids: string[] | 'all') => api.post('/api/notifications/read', { ids }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      void qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  return (
    <div className="page-narrow">
      <div className="section-head">
        <h1>{t('notifications.title')}</h1>
        {q.data.unread > 0 && (
          <button className="btn btn-small" onClick={() => mark.mutate('all')}>
            {t('notifications.markAll')}
          </button>
        )}
      </div>
      {q.data.items.length === 0 ? (
        <div className="rows">
          <p className="empty">{t('notifications.empty')}</p>
        </div>
      ) : (
        <ul className="rows">
          {q.data.items.map((n) => (
            <li key={n.id}>
              <Link className="row" to={n.link ?? '/'} onClick={() => !n.read && mark.mutate([n.id])}>
                <span
                  aria-hidden="true"
                  style={{ width: 10, height: 10, borderRadius: 5, flex: 'none', background: n.read ? 'transparent' : 'var(--brass)', border: n.read ? '1px solid var(--line-strong)' : 0 }}
                />
                <div className="row-main">
                  <div className="row-title" style={{ fontWeight: n.read ? 500 : 700 }}>
                    {text(n)}
                    {!n.read && <span className="visually-hidden"> {t('notifications.newItem')}</span>}
                  </div>
                  <div className="row-sub">{dateTime(n.createdAt)}</div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
